import { useMemo } from 'react';
import { useMutation } from '@tanstack/react-query';

import { Product } from '@setvi/shared/interfaces';

import { getProductMetadataMutation } from 'Services';
import { useAppContext } from 'Providers/AppProvider/AppContext';
import { removeEmptyValues } from 'componentsV2/product/details/content/subpages/sales-enablement/utils';

export const useProductMetadata = ({ product }: { product?: Product }) => {
  const { user } = useAppContext();

  const skipMetadataApi = useMemo(
    () => product?.sku === product?.manufacturerSku && !user?.IsManufacturer,
    [product, user]
  );

  const metadataParams = useMemo(
    () => ({
      manufacturerName: product?.vendor?.name,
      manufacturerSku: user?.IsManufacturer
        ? product?.sku
        : product?.manufacturerSku,
      ...(product?.name && { productName: product?.name })
    }),
    [product, user]
  );

  const {
    mutateAsync: getMetadata,
    isLoading,
    status,
    error
  } = useMutation({
    ...getProductMetadataMutation(),
    onError: () => {}
  });

  const errorCode = useMemo(() => (error as any)?.status, [error]);

  const databaseData = useMemo(
    () =>
      JSON.stringify({
        ...(product?.name && { name: product?.name || '' }),
        ...(product?.images && { images: product?.images || '' }),
        ...(product?.manufacturerSku && {
          manufacturerSku: product?.manufacturerSku || ''
        }),
        ...(product?.vendor && { vendor: product?.vendor || '' }),
        ...(product?.custom && { custom: product?.custom || '' })
      }),
    [product]
  );

  const handleMetadata = async (forceRefresh?: boolean) => {
    if (skipMetadataApi) return databaseData;

    const metadata = await getMetadata({ ...metadataParams, forceRefresh })
      .then((res: any) => res?.data || res)
      .catch(() => false);

    if (!metadata?.result) return '';
    if (metadata?.result?.for_review) return databaseData;

    const data = removeEmptyValues(metadata.result);

    return JSON.stringify(data);
  };

  const handleExternalMetadata = async (p: Product) => {
    if (p?.sku === p?.manufacturerSku) {
      return JSON.stringify({
        ...(p?.name && { name: p?.name || '' }),
        ...(p?.images && { images: p?.images || '' }),
        ...(p?.manufacturerSku && {
          manufacturerSku: p?.manufacturerSku || ''
        }),
        ...(p?.vendor && { vendor: p?.vendor || '' }),
        ...(p?.custom && { custom: p?.custom || '' })
      });
    }

    const metadata = await getMetadata({
      manufacturerName: p?.vendor?.name,
      manufacturerSku: user?.IsManufacturer ? p?.sku : p?.manufacturerSku,
      ...(p?.name && { productName: p?.name })
    });

    if (!metadata?.result) return '';
    if (metadata?.result?.for_review)
      return JSON.stringify({
        ...(p?.name && { name: p?.name || '' }),
        ...(p?.images && { images: p?.images || '' }),
        ...(p?.manufacturerSku && {
          manufacturerSku: p?.manufacturerSku || ''
        }),
        ...(p?.vendor && { vendor: p?.vendor || '' }),
        ...(p?.custom && { custom: p?.custom || '' })
      });

    const data = removeEmptyValues(metadata.result);

    return JSON.stringify(data);
  };

  const metadataFetched = useMemo(() => status === 'success', [status]);

  return {
    error,
    isLoading,
    errorCode,
    metadataFetched,
    metadataParams,
    skipMetadataApi,
    handleMetadata,
    handleExternalMetadata
  };
};
